// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import type { ThemeMode } from "../../context/AppThemeContext";

export const APPEARANCE_PREFERENCE_KEY = "appearance";

export interface AppearanceSyncState {
  hydrated: boolean;
  dirty: boolean;
  lastSynced: ThemeMode | null;
}

export interface AppearanceSyncDecision {
  state: AppearanceSyncState;
  apply: ThemeMode | null;
  write: ThemeMode | null;
}

export function parseAppearanceValue(value: string | null | undefined): ThemeMode | null {
  if (value === "light" || value === "dark" || value === "system") {
    return value;
  }
  return null;
}

export function onAppearanceHydrated(
  state: AppearanceSyncState,
  local: ThemeMode,
  remote: ThemeMode | null,
): AppearanceSyncDecision {
  if (state.dirty) {
    return {
      state: { hydrated: true, dirty: false, lastSynced: local },
      apply: null,
      write: local,
    };
  }

  if (remote !== null && remote !== local) {
    return {
      state: { hydrated: true, dirty: false, lastSynced: remote },
      apply: remote,
      write: null,
    };
  }

  return {
    state: { hydrated: true, dirty: false, lastSynced: remote ?? local },
    apply: null,
    write: null,
  };
}

export function onAppearanceChanged(
  state: AppearanceSyncState,
  mode: ThemeMode,
): AppearanceSyncDecision {
  if (!state.hydrated) {
    return {
      state: { ...state, dirty: true },
      apply: null,
      write: null,
    };
  }

  if (state.lastSynced === mode) {
    return { state, apply: null, write: null };
  }

  return {
    state: { hydrated: true, dirty: false, lastSynced: mode },
    apply: null,
    write: mode,
  };
}

export function onAppearanceWriteFailed(state: AppearanceSyncState): AppearanceSyncState {
  return { ...state, lastSynced: null };
}
